import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { cargoAPI, bookingAPI, paymentAPI, messageAPI } from "@/lib/api";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import {
  CargoHeader,
  CargoDetailsGrid,
  RouteMap,
  CargoSpecifications,
  CargoBookingSidebar,
  CargoBookingFormStep,
  CargoPaymentStep,
  CargoSuccessStep,
  CargoChatStep
} from "@/components/cargo";

// Google OAuth URL
const getGoogleAuthUrl = () => {
  const redirectUrl = window.location.origin + '/';
  return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// Load Razorpay script
const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [paymentStep, setPaymentStep] = useState("form");
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  
  const [bookingForm, setBookingForm] = useState({
    quantity: "",
    shipper_name: user?.name || "",
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchListing = useCallback(async () => {
    try {
      const response = await cargoAPI.getById(id);
      setListing(response.data);
    } catch (error) {
      toast.error("Listing not found");
      navigate("/browse");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchMessages = useCallback(async () => {
    if (!currentBooking) return;
    try {
      const response = await messageAPI.getMessages(currentBooking.id);
      setMessages(response.data);
      scrollToBottom();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch messages:', error.message);
      }
    }
  }, [currentBooking]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  useEffect(() => {
    if (currentBooking?.payment_status === "paid") {
      fetchMessages();
    }
  }, [currentBooking, fetchMessages]);

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Please sign in to book");
      window.location.href = getGoogleAuthUrl();
      return;
    }
    
    if (parseFloat(bookingForm.quantity) > listing.available_capacity) {
      toast.error(`Maximum capacity is ${listing.available_capacity} kg`);
      return;
    }
    
    setBookingLoading(true);
    
    try {
      const response = await bookingAPI.create({
        listing_id: id,
        listing_type: "cargo",
        quantity: parseFloat(bookingForm.quantity),
        shipper_name: bookingForm.shipper_name,
      });
      
      setCurrentBooking(response.data);
      setPaymentStep("payment");
      toast.success("Booking created! Please complete payment.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Booking failed");
    } finally {
      setBookingLoading(false);
    }
  };

  const handlePayment = async () => {
    const loaded = await loadRazorpay();
    
    if (!loaded) {
      handleMockPayment();
      return;
    }

    try {
      const orderResponse = await paymentAPI.createOrder(currentBooking.id);
      const orderData = orderResponse.data;

      if (orderData.mock) {
        handleMockPayment();
        return;
      }

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Setuxpress",
        description: `Booking for ${listing.origin_city} to ${listing.destination_city}`,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await paymentAPI.verifyPayment({
              booking_id: currentBooking.id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            
            setCurrentBooking(verifyResponse.data.booking);
            setPaymentStep("success");
            toast.success("Payment successful!");
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: user?.name,
          email: user?.email,
        },
        theme: {
          color: "#0F172A",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error("Failed to initiate payment");
    }
  };

  const handleMockPayment = async () => {
    setBookingLoading(true);
    try {
      const verifyResponse = await paymentAPI.verifyPayment({
        booking_id: currentBooking.id,
        razorpay_payment_id: `pay_mock_${Date.now()}`,
        razorpay_order_id: `order_mock_${Date.now()}`,
        razorpay_signature: "mock_signature",
      });
      
      setCurrentBooking(verifyResponse.data.booking);
      setPaymentStep("success");
      toast.success("Payment successful! (Test Mode)");
    } catch (error) {
      toast.error("Payment verification failed");
    } finally {
      setBookingLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await messageAPI.send(currentBooking.id, newMessage);
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleGoToDashboard = () => {
    setBookingOpen(false);
    navigate(user?.user_type === "partner" ? "/partner" : "/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!listing) return null;

  const totalAmount = bookingForm.quantity ? parseFloat(bookingForm.quantity) * listing.price_per_kg : 0;
  const platformFee = totalAmount * 0.02;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-new.png" alt="Setuxpress" className="h-10" />
              
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to="/browse" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Listings</span>
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <CargoHeader listing={listing} />
            <CargoDetailsGrid listing={listing} />
            <RouteMap listing={listing} />
            <CargoSpecifications listing={listing} />
          </div>

          {/* Booking Sidebar */}
          <CargoBookingSidebar
            listing={listing}
            bookingOpen={bookingOpen}
            setBookingOpen={setBookingOpen}
            user={user}
          >
            {paymentStep === "form" && (
              <CargoBookingFormStep
                listing={listing}
                bookingForm={bookingForm}
                setBookingForm={setBookingForm}
                totalAmount={totalAmount}
                platformFee={platformFee}
                bookingLoading={bookingLoading}
                onSubmit={handleBooking}
              />
            )}

            {paymentStep === "payment" && currentBooking && (
              <CargoPaymentStep
                currentBooking={currentBooking}
                bookingLoading={bookingLoading}
                onPayment={handlePayment}
              />
            )}

            {paymentStep === "success" && currentBooking && (
              <CargoSuccessStep
                currentBooking={currentBooking}
                onChat={() => setPaymentStep("chat")}
                onDashboard={handleGoToDashboard}
              />
            )}

            {paymentStep === "chat" && currentBooking && (
              <CargoChatStep
                messages={messages}
                user={user}
                newMessage={newMessage}
                setNewMessage={setNewMessage}
                sendingMessage={sendingMessage}
                onSend={handleSendMessage}
                onBack={() => setPaymentStep("success")}
                messagesEndRef={messagesEndRef}
              />
            )}
          </CargoBookingSidebar>
        </div>
      </div>
    </div>
  );
}
